import React, { useEffect, useRef, useState } from 'react';

import { cn } from 'revibe-ui';
import Carousel from 'react-gallery-carousel';

import { Image } from 'modules/common/components/ImageLazyload';
import { BREAKPOINTS, Media } from 'modules/common/components/Media';
import { isBrowserEnv } from 'modules/common/utils/env';

import { LeftArrowIcon } from 'ui/icons/LeftArrowIcon';
import { MinimizeIcon } from 'ui/icons/MinimizeIcon';
import { RightArrowIcon } from 'ui/icons/RightArrowIcon';
import { ZoomIcon } from 'ui/icons/ZoomIcon';
import { MarketplaceAPI } from 'revibe-api';

type Props = {
  photos: MarketplaceAPI.ItemPhoto[];
  itemName: string;
};

export const ItemPhoto = ({ photos, itemName }: Props) => {
  const images = photos.map((photo) => ({
    src: photo.link,
    srcset: `${photo.medium_link} 400w,${photo.large_link} 700w, ${photo.link} 2000w`,
    sizes: '(max-width: 2000px) 400px, (max-width: 2000px) 700px, 2000px',
    alt: itemName,
    thumbnail: photo.mini_link,
  }));
  const zoomLevel = 2;
  const magnifierWidth = 300;
  const magnifierHeight = 300;
  const [[x, y], setXY] = useState([0, 0]);
  const [[imgWidth, imgHeight], setSize] = useState([0, 0]);
  const [showMagnifier, setShowMagnifier] = useState<string | null>(null);
  const photosRefs = useRef<Array<HTMLDivElement | null>>([]);
  useEffect(() => {
    photosRefs.current = photosRefs.current.slice(0, photos.length);
  }, [photos]);

  return (
    <>
      <Media greaterThanOrEqual={BREAKPOINTS.DESKTOP}>
        <div className="grid grid-cols-[96px_minmax(0,1fr)]">
          <div
            className={
              'sticky top-[3rem] flex h-fit w-[96px] flex-col gap-[1px] border-b border-black bg-black'
            }
          >
            {photos.map((p, i) => (
              <div
                onClick={() =>
                  photosRefs.current[i]?.scrollIntoView({ behavior: 'smooth' })
                }
                key={p.id}
                className="pointer box-border cursor-pointer bg-[#f3f3f3]"
              >
                <Image src={p.mini_link} alt={itemName} />
              </div>
            ))}
          </div>
          <div className="border-l border-black">
            {photos.map((p, i) => (
              <div
                key={p.id}
                style={{
                  position: 'relative',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  width: '100%',
                }}
                className="bg-[#f3f3f3]"
                ref={(el) => (photosRefs.current[i] = el)}
              >
                <Image
                  src={p.large_link}
                  onMouseEnter={(e) => {
                    // update image size and turn-on magnifier
                    const elem = e.currentTarget;
                    const { width, height } = elem.getBoundingClientRect();
                    setSize([width, height]);
                    setShowMagnifier(p.id);
                  }}
                  onMouseMove={(e) => {
                    // update cursor position
                    if (isBrowserEnv()) {
                      const elem = e.currentTarget;
                      const { top, left } = elem.getBoundingClientRect();

                      // calculate cursor position on the image
                      const x = e.pageX - left - window.pageXOffset;
                      const y = e.pageY - top - window.pageYOffset;
                      setXY([x, y]);
                    }
                  }}
                  onMouseLeave={() => {
                    // close magnifier
                    setShowMagnifier(null);
                  }}
                  alt={itemName}
                />

                <div
                  style={{
                    display: showMagnifier === p.id ? '' : 'none',
                    position: 'absolute',
                    pointerEvents: 'none',
                    height: `${magnifierHeight}px`,
                    width: `${magnifierWidth}px`,
                    top: `${y - magnifierHeight / 2}px`,
                    left: `${x - magnifierWidth / 2}px`,
                    opacity: '1', // reduce opacity so you can verify position
                    border: '1px solid var(--black)',
                    backgroundColor: 'white',
                    backgroundImage: `url('${p.large_link}')`,
                    backgroundRepeat: 'no-repeat',
                    //calculate zoomed image size
                    backgroundSize: `${imgWidth * zoomLevel}px ${
                      imgHeight * zoomLevel
                    }px`,
                    //calculate position of zoomed image.
                    backgroundPositionX: `${
                      -x * zoomLevel + magnifierWidth / 2
                    }px`,
                    backgroundPositionY: `${
                      -y * zoomLevel + magnifierHeight / 2
                    }px`,
                  }}
                ></div>
              </div>
            ))}
          </div>
        </div>
      </Media>
      <Media at={BREAKPOINTS.MOBILE}>
        <Carousel
          images={images}
          style={{ height: 380 }}
          leftIcon={
            <LeftArrowIcon
              className={cn(
                'child:stroke-black m-4 h-8 w-8 cursor-pointer border border-black bg-white p-1',
                photos.length < 2 ? 'hidden' : 'block'
              )}
            />
          }
          rightIcon={
            <RightArrowIcon
              className={cn(
                'child:stroke-black m-4 h-8 w-8 cursor-pointer border border-black bg-white p-1',
                photos.length < 2 ? 'hidden' : 'block'
              )}
            />
          }
          maxIcon={
            <ZoomIcon className="child:stroke-black m-4 h-8 w-8 cursor-pointer border border-black bg-white p-1" />
          }
          // @ts-ignore
          minIcon={
            <MinimizeIcon className="child:stroke-black m-4 h-8 w-8 cursor-pointer border border-black bg-white p-1" />
          }
          canAutoPlay={false}
          thumbnailWidth="48px"
          thumbnailHeight="48px"
          hasIndexBoard={false}
          hasThumbnails={false}
          hasThumbnailsAtMax={true}
          className="border-b border-black"
        />
      </Media>
    </>
  );
};
